import type { ProjectMemberRole, SettingsProject } from "@/lib/projects/types";

/**
 * Direct project role wins. Group membership only grants access.
 * A group role of manager still applies when that person has no own row yet.
 */
export const projectMemberRole = (
	userId: string,
	users: readonly { readonly id: string; readonly removed?: boolean; readonly role?: string }[],
	groups: readonly { readonly id: string; readonly removed?: boolean; readonly role?: string }[],
	userGroupIds: readonly string[]
): ProjectMemberRole | null => {
	const direct = users.find((user) => user.id === userId && !user.removed);
	if (direct?.role === "manager" || direct?.role === "member") {
		return direct.role;
	}
	const ids = new Set(userGroupIds);
	const matched = groups.filter((group) => !group.removed && ids.has(group.id));
	if (matched.length === 0) {
		return null;
	}
	return matched.some((group) => group.role === "manager") ? "manager" : "member";
};

export const isProjectInCreateCatalog = (
	project: SettingsProject,
	userId: string,
	userGroupIds: readonly string[]
): boolean => project.useAsDefault || isUserOnProject(project, userId, userGroupIds);

export const isUserOnProject = (project: SettingsProject, userId: string, userGroupIds: readonly string[]): boolean => {
	if (project.useAsDefault) {
		return false;
	}
	if (project.users.some((user) => user.id === userId && !user.removed)) {
		return true;
	}
	if (userGroupIds.length === 0) {
		return false;
	}
	const groupIds = new Set(userGroupIds);
	return project.groups.some((group) => !group.removed && groupIds.has(group.id));
};

export const isGroupOnProject = (project: SettingsProject, groupId: string): boolean => {
	if (project.useAsDefault) {
		return false;
	}
	return project.groups.some((group) => group.id === groupId && !group.removed);
};

export const listProjectMemberIds = (
	project: SettingsProject,
	users: readonly { readonly groupIds: readonly string[]; readonly id: string; readonly removed: boolean }[]
): readonly string[] => {
	const ids: string[] = [];
	const seen = new Set<string>();
	for (const user of users) {
		if (user.removed || !isUserOnProject(project, user.id, user.groupIds)) {
			continue;
		}
		seen.add(user.id);
		ids.push(user.id);
	}
	for (const member of project.users) {
		if (member.removed || seen.has(member.id)) {
			continue;
		}
		seen.add(member.id);
		ids.push(member.id);
	}
	return ids;
};

export const countUserProjects = (
	projects: readonly SettingsProject[],
	userId: string,
	userGroupIds: readonly string[]
): number => {
	let count = 0;
	for (const project of projects) {
		if (isUserOnProject(project, userId, userGroupIds)) {
			count += 1;
		}
	}
	return count;
};
